import { useCallback } from 'react';

import { useSelector } from 'react-redux';

import useLocalStorage from '@/hooks/useLocalStorage';
import { selectAccountSlug } from '@/redux/session/selectors';

type FrecencyEntry = { visitCount: number; lastVisit: number };
type FrecencyData = Record<string, FrecencyEntry>;

const HOUR = 3_600_000;
const DAY = 24 * HOUR;

/** Returns a weight (0–100) that decays in tiers: 4 h → 1 d → 3 d → 7 d → 30 d → 0. */
const recencyWeight = (lastVisit: number): number => {
  const age = Date.now() - lastVisit;
  if (age < 4 * HOUR) {
    return 100;
  }
  if (age < DAY) {
    return 70;
  }
  if (age < 3 * DAY) {
    return 50;
  }
  if (age < 7 * DAY) {
    return 30;
  }
  if (age < 30 * DAY) {
    return 10;
  }
  return 0;
};

const getEntryScore = (entry: FrecencyEntry): number =>
  entry.visitCount * recencyWeight(entry.lastVisit);

type UseFrecencyReturn = { trackVisit: (path: string) => void; getScore: (path: string) => number };

/** Tracks page visit frequency + recency in localStorage and exposes a scoring function. */
export const useFrecency = (): UseFrecencyReturn => {
  const slug = useSelector(selectAccountSlug);
  const storageKey = slug ? `commandPalette_frecency_${slug}` : undefined;

  const [data, setData] = useLocalStorage<FrecencyData>(storageKey, {});

  const trackVisit = useCallback(
    (path: string) => {
      setData((prev) => {
        const updated: FrecencyData = {
          ...prev,
          [path]: { visitCount: (prev[path]?.visitCount ?? 0) + 1, lastVisit: Date.now() },
        };

        // Lazy pruning: remove zero-score entries (keep the current path)
        return Object.fromEntries(
          Object.entries(updated).filter(([key, val]) => key === path || getEntryScore(val) > 0)
        );
      });
    },
    [setData]
  );

  const getScore = useCallback(
    (path: string): number => {
      const entry = data[path];
      return entry ? getEntryScore(entry) : 0;
    },
    [data]
  );

  return { trackVisit, getScore };
};
